import { useEffect, useRef, useState } from 'react';

/**
 * The sign-in captcha, whichever provider the institute configured.
 *
 * The settings page has always offered Cloudflare Turnstile, Google reCAPTCHA
 * and hCaptcha, but only Turnstile was ever implemented. Choosing one of the
 * other two, saving a valid site key and secret, and reloading the sign-in page
 * produced nothing at all — no widget, and no server-side verification either,
 * so the protection was silently absent rather than visibly broken.
 *
 * All three render the same way: load the provider's script with an explicit
 * onload, then call `render(element, { sitekey, callback })`. The differences
 * are the script URL and the global it installs, so they live in one table and
 * everything else is shared.
 */

export type CaptchaProvider = 'none' | 'turnstile' | 'recaptcha' | 'recaptcha_v3' | 'hcaptcha';

interface RenderOptions {
    sitekey: string;
    callback: (token: string) => void;
    'expired-callback'?: () => void;
    'error-callback'?: () => void;
    theme?: string;
}

interface CaptchaApi {
    render: (el: HTMLElement, opts: RenderOptions) => string;
    reset: (id?: string) => void;
    // v3 only: no widget, no interaction — a token on demand.
    ready?: (cb: () => void) => void;
    execute?: (siteKey: string, opts: { action: string }) => Promise<string>;
}

declare global {
    interface Window {
        turnstile?: CaptchaApi;
        grecaptcha?: CaptchaApi;
        hcaptcha?: CaptchaApi;
        [key: string]: unknown;
    }
}

const PROVIDERS: Record<Exclude<CaptchaProvider, 'none'>, {
    global: 'turnstile' | 'grecaptcha' | 'hcaptcha';
    scriptId: string;
    src: (onload: string, siteKey: string) => string;
}> = {
    turnstile: {
        global: 'turnstile',
        scriptId: 'cf-turnstile-script',
        src: (onload) => `https://challenges.cloudflare.com/turnstile/v0/api.js?onload=${onload}`,
    },
    recaptcha: {
        global: 'grecaptcha',
        scriptId: 'g-recaptcha-script',
        // render=explicit, or reCAPTCHA hunts for .g-recaptcha elements at load
        // time and finds none — this one is created by React afterwards.
        src: (onload) => `https://www.google.com/recaptcha/api.js?onload=${onload}&render=explicit`,
    },
    // v3 is a different API wearing the same name. The script URL chooses the
    // mode and cannot serve both: asking a v3 key to render a checkbox is what
    // produces "ERROR for site owner: Invalid key type", and asking a v2 key to
    // execute() is rejected as an invalid site key. Confirmed against a live
    // v3 key on the real origin, which is the only place the key is valid.
    recaptcha_v3: {
        global: 'grecaptcha',
        scriptId: 'g-recaptcha-v3-script',
        src: (onload, siteKey) => `https://www.google.com/recaptcha/api.js?onload=${onload}&render=${siteKey}`,
    },
    hcaptcha: {
        global: 'hcaptcha',
        scriptId: 'hcaptcha-script',
        src: (onload) => `https://js.hcaptcha.com/1/api.js?onload=${onload}&render=explicit`,
    },
};

export function captchaIsUsable(config: {
    enabled?: boolean;
    provider?: CaptchaProvider;
    site_key?: string;
    supported?: boolean;
}) {
    return Boolean(
        config?.enabled
        && config.provider
        && config.provider !== 'none'
        && config.site_key
        && config.supported
    );
}

interface Props {
    provider: CaptchaProvider;
    siteKey: string;
    onToken: (token: string | null) => void;
    /** Change this to clear a solved challenge — after a failed sign-in. */
    resetKey?: number;
    className?: string;
}

export default function CaptchaWidget({ provider, siteKey, onToken, resetKey = 0, className }: Props) {
    const holder = useRef<HTMLDivElement>(null);
    const widgetId = useRef<string | null>(null);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);

    const spec = provider !== 'none' ? PROVIDERS[provider] : null;

    // ── load the provider's script, once per page ───────────────────────────
    useEffect(() => {
        if (!spec || !siteKey) return;
        if (window[spec.global]) { setReady(true); return; }

        const existing = document.getElementById(spec.scriptId);
        if (existing) return;                      // already loading; its onload will fire

        const onloadName = `onCaptchaLoad_${spec.global}`;
        window[onloadName] = () => setReady(true);

        const script = document.createElement('script');
        script.id = spec.scriptId;
        script.src = spec.src(onloadName, siteKey);
        script.async = true;
        script.defer = true;
        // A blocked or unreachable provider must not leave a blank gap with no
        // explanation — on a lab network these domains are sometimes filtered.
        script.onerror = () => setFailed(true);
        document.head.appendChild(script);
    }, [spec, siteKey]);

    // ── v3: no widget. Ask for a token, and keep it fresh ──────────────────
    //
    // A v3 token is good for about two minutes. Fetching one only at mount
    // would hand the server an expired token from anyone who reads the page
    // before signing in, so it is refreshed well inside that window.
    useEffect(() => {
        if (provider !== 'recaptcha_v3' || !ready || !siteKey) return;
        let cancelled = false;

        const run = async () => {
            const api = window.grecaptcha as CaptchaApi | undefined;
            if (!api?.execute) return;
            try {
                await new Promise<void>((resolve) => (api.ready ? api.ready(resolve) : resolve()));
                const token = await api.execute(siteKey, { action: 'login' });
                if (!cancelled) onToken(token || null);
            } catch {
                // A v2 key asked to execute lands here ("Invalid site key"),
                // which means the provider is set to v3 and the key is not.
                if (!cancelled) { setFailed(true); onToken(null); }
            }
        };
        void run();
        const timer = setInterval(run, 90_000);
        return () => { cancelled = true; clearInterval(timer); };
    }, [provider, ready, siteKey, resetKey]);     // eslint-disable-line react-hooks/exhaustive-deps

    // ── render the widget ───────────────────────────────────────────────────
    useEffect(() => {
        if (provider === 'recaptcha_v3') return;   // nothing to draw
        if (!spec || !ready || !holder.current || widgetId.current) return;
        const api = window[spec.global] as CaptchaApi | undefined;
        if (!api?.render) return;
        try {
            widgetId.current = api.render(holder.current, {
                sitekey: siteKey,
                callback: (token: string) => onToken(token),
                'expired-callback': () => onToken(null),
                'error-callback': () => onToken(null),
                // Match the portal theme — a hard-coded light widget is a bright
                // rectangle in the middle of a dark sign-in card.
                theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
            });
        } catch {
            // A bad site key throws here rather than rejecting later.
            setFailed(true);
        }
    }, [spec, ready, siteKey, onToken]);

    // ── clear a solved challenge when the caller asks ───────────────────────
    useEffect(() => {
        if (provider === 'recaptcha_v3') return;   // the effect above re-executes
        if (!resetKey || !spec || !widgetId.current) return;
        const api = window[spec.global] as CaptchaApi | undefined;
        try { api?.reset(widgetId.current); } catch { /* already gone */ }
        onToken(null);
    }, [resetKey]);                              // eslint-disable-line react-hooks/exhaustive-deps

    if (!spec || !siteKey) return null;

    if (failed) {
        return (
            <p className={`text-xs text-amber-600 dark:text-amber-400 ${className || ''}`}>
                {provider === 'recaptcha_v3'
                    ? 'The security check could not run. If this is a v2 key, choose "Google reCAPTCHA v2" in Settings — a v2 key cannot be used in v3 mode.'
                    : 'The security check could not load. If this keeps happening, your network may be blocking it — tell your administrator.'}
            </p>
        );
    }

    // v3 shows nothing by design — it scores the visit in the background. Say
    // so, or the sign-in form looks like it simply forgot the captcha.
    if (provider === 'recaptcha_v3') {
        return (
            <p className={`text-xs text-slate-500 dark:text-slate-400 ${className || ''}`}>
                Protected by reCAPTCHA.
            </p>
        );
    }

    // min-height holds the space before the widget arrives, so the form does
    // not jump as it loads.
    return <div ref={holder} className={`min-h-[65px] ${className || ''}`} />;
}
